import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import { PlusIcon } from 'lucide-react';
import { type ReactNode, type RefObject, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fuzzyMatch } from '../../../common/functions/fuzzy-match';
import { ComboboxItem, ComboboxList, useComboboxFilteredItems } from './combobox';

export type VirtualizerHandle = Virtualizer<HTMLDivElement, Element>;

/**
 * Builds an `onItemHighlighted` handler for virtualized comboboxes. Base-ui scrolls
 * the highlighted DOM node into view, but virtualized rows past the overscan window have
 * no DOM node, so its `scrollIntoView` no-ops. This handler scrolls the virtualizer for
 * every keyboard move and for programmatic highlight changes (e.g. opening with a
 * pre-selected value). Pointer-driven highlights are skipped — they already correspond
 * to a mounted, visible row. `align: 'auto'` keeps the call a no-op when the index is
 * already visible.
 */
export const buildVirtualizedHighlightHandler =
  (virtualizerRef: RefObject<VirtualizerHandle | null>) =>
  (_value: unknown, details: { reason: 'keyboard' | 'pointer' | 'none'; index: number }) => {
    const virtualizer = virtualizerRef.current;
    const count = virtualizer?.options?.count;
    if (!virtualizer || count == null || details.index < 0 || details.reason === 'pointer') {
      return;
    }
    queueMicrotask(() => {
      virtualizer.scrollToIndex(details.index, { align: 'auto' });
    });
  };

export interface CreatableMarker {
  __create: string;
}

export const isCreatable = (item: unknown): item is CreatableMarker => {
  return typeof item === 'object' && item !== null && '__create' in item;
};

export type QueryProps =
  | {
      /** Controlled input value. When supplied with `onQueryChange`, the preset forwards typing to the caller for server-side filtering and trusts `items` as the rendered set. Whether `items` clears between queries is up to the caller: clearing shows the loading state in the empty slot, keeping stale items keeps them visible until the new page arrives. */
      query: string;
      /** Controlled input change handler. Must be supplied together with `query`. */
      onQueryChange: (query: string) => void;
    }
  | {
      query?: undefined;
      onQueryChange?: undefined;
    };

export type CreatableProps<T> =
  | {
      /** When supplied, a synthetic "Create" option is rendered for a non-matching query. Selecting it calls `onCreate(query)` and commits the returned value via `onValueChange`. */
      onCreate: (query: string) => Promise<T> | T;
      /** Optional renderer for the synthetic create option. Receives the current query. Defaults to `t('combobox-create', { query })`. */
      renderCreateOption?: (query: string) => ReactNode;
    }
  | {
      onCreate?: undefined;
      renderCreateOption?: undefined;
    };

export const ROW_OVERSCAN = 10;
export const TAIL_MARGIN = '120px';
export const DEFAULT_ESTIMATE_SIZE = 32;
/** Popup max height in px. Drives both the height cap and the scrollbar-suppression branch. */
export const MAX_LIST_PX = 288;
/** Matches the `p-1` padding on ComboboxList. Used as the scrollPadding hint for the virtualizer
 * (tanstack-virtual ignores CSS scroll-padding) and to size the height buffer. */
const LIST_PADDING_PX = 4;
const HEIGHT_BUFFER_PX = LIST_PADDING_PX * 2;
/** Vitest sets `process.env.VITEST=true`; `globalThis.process` is undefined in the browser. */
const IS_VITEST = (globalThis as { process?: { env?: { VITEST?: string } } }).process?.env?.VITEST === 'true';

export const useCreatableHelpers = <T,>(
  items: readonly T[],
  getLabel: (item: T) => string,
  itemToStringLabel: ((item: T) => string) | undefined,
  onCreate: ((query: string) => Promise<T> | T) | undefined,
  effectiveQuery: string
) => {
  const getLabelRef = useRef(getLabel);
  getLabelRef.current = getLabel;
  const itemToStringLabelRef = useRef(itemToStringLabel);
  itemToStringLabelRef.current = itemToStringLabel;

  const itemsForView = useMemo(() => {
    if (!onCreate) {
      return items;
    }

    const trimmed = effectiveQuery.trim();
    if (!trimmed) {
      return items;
    }

    const lowered = trimmed.toLowerCase();
    const exactMatch = items.some((item) => getLabelRef.current(item).trim().toLowerCase() === lowered);
    if (exactMatch) {
      return items;
    }

    return [...items, { __create: trimmed } as unknown as T];
  }, [items, onCreate, effectiveQuery]);

  const localFilter = useCallback(
    (item: T, q: string) => (isCreatable(item) ? true : fuzzyMatch(getLabelRef.current(item), q)),
    []
  );

  const stableItemToStringLabel = useCallback((item: T | null) => {
    if (!item) {
      return '';
    }
    if (isCreatable(item)) {
      return item.__create;
    }
    return itemToStringLabelRef.current ? itemToStringLabelRef.current(item) : getLabelRef.current(item);
  }, []);

  return { itemsForView, localFilter, stableItemToStringLabel };
};

interface VirtualizedListProps<T> {
  items: readonly T[];
  getKey: (item: T) => string | number;
  getLabel: (item: T) => string;
  estimateSize?: number;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore?: () => void;
  renderCreateOption?: (query: string) => ReactNode;
  isServerFilter: boolean;
  virtualizerRef?: RefObject<VirtualizerHandle | null>;
}

export const VirtualizedList = <T,>(props: VirtualizedListProps<T>) => {
  return props.isServerFilter ? (
    <VirtualRows<T>
      renderItems={props.items}
      getKey={props.getKey}
      getLabel={props.getLabel}
      estimateSize={props.estimateSize}
      isLoading={props.isLoading}
      hasMore={props.hasMore}
      onLoadMore={props.onLoadMore}
      renderCreateOption={props.renderCreateOption}
      virtualizerRef={props.virtualizerRef}
    />
  ) : (
    <LocallyFilteredVirtualRows<T> {...props} />
  );
};

const LocallyFilteredVirtualRows = <T,>({
  getKey,
  getLabel,
  estimateSize,
  isLoading,
  hasMore,
  onLoadMore,
  renderCreateOption,
  virtualizerRef,
}: VirtualizedListProps<T>) => {
  const renderItems = useComboboxFilteredItems<T>();
  return (
    <VirtualRows<T>
      renderItems={renderItems}
      getKey={getKey}
      getLabel={getLabel}
      estimateSize={estimateSize}
      isLoading={isLoading}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      renderCreateOption={renderCreateOption}
      virtualizerRef={virtualizerRef}
    />
  );
};

interface VirtualRowsProps<T> {
  renderItems: readonly T[];
  getKey: (item: T) => string | number;
  getLabel: (item: T) => string;
  estimateSize?: number;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore?: () => void;
  renderCreateOption?: (query: string) => ReactNode;
  virtualizerRef?: RefObject<VirtualizerHandle | null>;
}

const VirtualRows = <T,>({
  renderItems,
  getKey,
  getLabel,
  estimateSize = DEFAULT_ESTIMATE_SIZE,
  isLoading,
  hasMore,
  onLoadMore,
  renderCreateOption,
  virtualizerRef,
}: VirtualRowsProps<T>) => {
  const { t } = useTranslation();

  const scrollElementRef = useRef<HTMLDivElement | null>(null);
  const tailRef = useRef<HTMLDivElement | null>(null);
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const virtualizer = useVirtualizer({
    count: renderItems.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => estimateSize,
    overscan: ROW_OVERSCAN,
    // tanstack-virtual doesn't read CSS scroll-padding, so emulate it via these options.
    scrollPaddingStart: 0,
    scrollPaddingEnd: LIST_PADDING_PX * 2,
  });

  useImperativeHandle(virtualizerRef, () => virtualizer, [virtualizer]);

  const handleScrollElementRef = useCallback(
    (element: HTMLDivElement | null) => {
      scrollElementRef.current = element;
      if (element) {
        virtualizer.measure();
      }
    },
    [virtualizer]
  );

  useEffect(() => {
    const tail = tailRef.current;
    const root = scrollElementRef.current;
    if (!tail || !root || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !isLoadingRef.current) {
          onLoadMoreRef.current?.();
        }
      },
      { root, rootMargin: TAIL_MARGIN }
    );

    observer.observe(tail);

    return () => observer.disconnect();
  }, [hasMore]);

  if (renderItems.length === 0) {
    return null;
  }

  // In jsdom, the real virtualizer sees a 0-height viewport and renders no items, so
  // any test that opens a combobox to assert on options would fail. Bypass virtualization in
  // vitest by rendering items inline. Consuming packages don't need per-test virtualizer mocks.
  // Production and storybook are unaffected.
  if (IS_VITEST) {
    return (
      <ComboboxList>
        {renderItems.map((item, index) => {
          if (isCreatable(item)) {
            return (
              <ComboboxItem
                key={`__create__${item.__create}`}
                index={index}
                value={item}
                title={renderCreateOption ? undefined : t('combobox-create', { query: item.__create })}
                aria-setsize={renderItems.length}
                aria-posinset={index + 1}
              >
                {renderCreateOption ? (
                  renderCreateOption(item.__create)
                ) : (
                  <>
                    <PlusIcon aria-hidden="true" />
                    <span className="truncate">{t('combobox-create', { query: item.__create })}</span>
                  </>
                )}
              </ComboboxItem>
            );
          }
          return (
            <ComboboxItem
              key={getKey(item)}
              index={index}
              value={item}
              aria-setsize={renderItems.length}
              aria-posinset={index + 1}
            >
              {getLabel(item)}
            </ComboboxItem>
          );
        })}
      </ComboboxList>
    );
  }

  const totalSize = virtualizer.getTotalSize();
  const needsScroll = totalSize > MAX_LIST_PX;
  // Clamp to content + small buffer so the scrollbar doesn't reserve a sub-pixel strip at small counts.
  const height = Math.min(MAX_LIST_PX, totalSize + HEIGHT_BUFFER_PX);

  return (
    <ComboboxList
      ref={handleScrollElementRef}
      className={needsScroll ? undefined : 'overflow-y-hidden'}
      style={{ height }}
    >
      <div role="presentation" className="relative w-full" style={{ height: totalSize }}>
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const item = renderItems[virtualItem.index];
          if (item === undefined) {
            return null;
          }
          const itemStyle = {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            height: estimateSize,
            transform: `translateY(${virtualItem.start}px)`,
          };
          if (isCreatable(item)) {
            return (
              <ComboboxItem
                key={`__create__${item.__create}`}
                index={virtualItem.index}
                value={item}
                style={itemStyle}
                title={renderCreateOption ? undefined : t('combobox-create', { query: item.__create })}
                aria-setsize={renderItems.length}
                aria-posinset={virtualItem.index + 1}
              >
                {renderCreateOption ? (
                  renderCreateOption(item.__create)
                ) : (
                  <>
                    <PlusIcon aria-hidden="true" />
                    <span className="truncate">{t('combobox-create', { query: item.__create })}</span>
                  </>
                )}
              </ComboboxItem>
            );
          }
          return (
            <ComboboxItem
              key={getKey(item)}
              index={virtualItem.index}
              value={item}
              style={itemStyle}
              aria-setsize={renderItems.length}
              aria-posinset={virtualItem.index + 1}
            >
              {getLabel(item)}
            </ComboboxItem>
          );
        })}
      </div>
      {hasMore && <div ref={tailRef} aria-hidden="true" style={{ height: 1 }} />}
      {isLoading && (
        <output aria-live="polite" className="block px-2 py-2 text-center text-gray-2 text-sm">
          {t('combobox-loading')}
        </output>
      )}
    </ComboboxList>
  );
};
